'use client';

import React from 'react';
import { cn } from '../../lib/utils';
import { Icon } from '@iconify/react';

// Main Technology interface for the structured data
export interface Technology {
  _id: string;
  name: string;
  iconName: string;
  brandColor?: string;
  textColor?: string;
}

// Support both fully structured Technology objects and simple strings for backward compatibility
export type TechnologyItem = Technology | string;

interface TechnologyTagProps {
  technology: Technology;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'filled' | 'outlined' | 'minimal';
}

/**
 * TechnologyTag component
 * Displays a technology with its proper branding colors and icon
 * Variants: filled (default), outlined, minimal
 * Sizes: sm, md (default), lg
 */
export function TechnologyTag({
  technology,
  className,
  size = 'md',
  variant = 'filled',
}: TechnologyTagProps) {
  // Apply opacity to brand color for better contrast while maintaining branding
  const applyColorOpacity = (color: string) => {
    // If color already has transparency, return as is
    if (color.length === 9 || color.startsWith('rgba')) return color;
    
    // Otherwise add 85% opacity (DD in hex)
    return `${color}DD`;
  };
  
  const { name, iconName, brandColor = '#4B5563', textColor = '#FFFFFF' } = technology;
  const adjustedBrandColor = applyColorOpacity(brandColor);
  
  // Size variants
  const sizeClasses = {
    sm: 'text-xs px-1.5 py-0.5 gap-1.5',
    md: 'text-xs px-2 py-1 gap-2',
    lg: 'text-sm px-2.5 py-1.5 gap-2.5',
  };
  
  // Icon size based on tag size
  const iconSize = {
    sm: 14,
    md: 16,
    lg: 18,
  };
  
  // Style variants for the container only
  const getVariantClasses = () => {
    switch (variant) {
      case 'filled':
        return {
          container: `text-[${textColor}]`,
        };
      case 'outlined':
        return {
          container: `border-[1.5px] text-[${brandColor}] bg-transparent`,
        };
      case 'minimal':
        return {
          container: 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-200',
        };
      default:
        return {
          container: `text-[${textColor}]`,
        };
    }
  };
  
  const variantClasses = getVariantClasses();
  
  return (
    <span 
      className={cn(
        'inline-flex items-center rounded-md font-medium transition-all',
        'hover:ring-1 hover:ring-white/30 hover:ring-offset-1 hover:ring-offset-transparent',
        sizeClasses[size],
        variantClasses.container,
        className
      )}
      style={{
        backgroundColor: variant === 'filled' ? adjustedBrandColor : variant === 'minimal' ? '' : 'transparent',
        color: variant === 'filled' ? textColor : variant === 'minimal' ? '' : brandColor,
        borderColor: variant === 'outlined' ? brandColor : 'transparent',
      }}
    >
      {iconName && (
        // Isolate the icon in its own container to prevent style inheritance
        <span className="inline-block" style={{ lineHeight: 0 }}>
          <Icon 
            icon={iconName} 
            width={iconSize[size]} 
            height={iconSize[size]} 
            // No classes, no styles - keep original icon colors
          />
        </span>
      )}
      <span>{name}</span>
    </span>
  );
}

// Simple tag for legacy string-based technologies
function SimpleTechnologyTag({
  name,
  className,
  size = 'md',
  variant = 'filled',
}: {
  name: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'filled' | 'outlined' | 'minimal';
}) {
  // Size variants
  const sizeClasses = {
    sm: 'text-xs px-1.5 py-0.5',
    md: 'text-xs px-2 py-1',
    lg: 'text-sm px-2.5 py-1.5',
  };
  
  // Default styling based on variant - more subtle than before
  const getVariantClasses = () => {
    switch (variant) {
      case 'filled':
        return 'bg-slate-100/90 dark:bg-slate-800/70 text-slate-700 dark:text-slate-300 hover:bg-slate-200/90 dark:hover:bg-slate-700/70';
      case 'outlined':
        return 'border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 bg-transparent hover:bg-slate-50/50 dark:hover:bg-slate-800/30';
      case 'minimal':
        return 'bg-slate-50/80 dark:bg-slate-800/40 text-slate-600 dark:text-slate-400 hover:bg-slate-100/80 dark:hover:bg-slate-700/40';
      default:
        return 'bg-slate-100/90 dark:bg-slate-800/70 text-slate-700 dark:text-slate-300 hover:bg-slate-200/90 dark:hover:bg-slate-700/70';
    }
  };
  
  return (
    <span 
      className={cn(
        'inline-flex items-center rounded-md font-medium transition-all',
        'hover:ring-1 hover:ring-slate-300/30 dark:hover:ring-slate-600/30',
        sizeClasses[size],
        getVariantClasses(),
        className
      )}
    >
      {name}
    </span>
  );
}

/**
 * TechnologyTagList component 
 * Displays a list of technology tags
 * Handles both legacy string technologies and new structured technology objects
 */
export interface TechnologyTagListProps {
  technologies: (TechnologyItem)[];
  className?: string;
  tagClassName?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'filled' | 'outlined' | 'minimal';
}

export function TechnologyTagList({
  technologies,
  className,
  tagClassName,
  size = 'md',
  variant = 'filled',
}: TechnologyTagListProps) {
  if (!technologies || technologies.length === 0) {
    return null;
  }
  
  return (
    <div className={cn('flex flex-wrap gap-1.5', className)}>
      {technologies.map((tech, index) => {
        // Handle both new structured technology objects and legacy strings
        if (typeof tech === 'string') {
          return (
            <SimpleTechnologyTag
              key={`tech-${index}`}
              name={tech}
              size={size}
              variant={variant}
              className={tagClassName}
            />
          );
        }
        
        // Make sure tech is a valid technology object with required properties
        if (tech && tech._id && tech.name) {
          return (
            <TechnologyTag
              key={tech._id}
              technology={tech}
              size={size}
              variant={variant}
              className={tagClassName}
            />
          );
        }
        
        // Fallback for invalid data
        return null;
      })}
    </div>
  );
} 